/**
 * HealthCheckDashboard Component
 * Main dashboard displaying system health status
 */

import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchHealthCheck, setAutoRefresh, clearError } from '../../store/healthSlice';
import HealthCheckCard from './HealthCheckCard';
import Loading from '../shared/Loading';
import type { ServiceType } from '../../types/health.types';
import './HealthCheckDashboard.css';

const HealthCheckDashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const { data, loading, error, lastUpdated, autoRefresh } = useAppSelector(
    (state) => state.health
  );

  // Auto-refresh functionality
  useEffect(() => {
    // Initial load
    dispatch(fetchHealthCheck());

    if (autoRefresh) {
      const interval = setInterval(() => {
        dispatch(fetchHealthCheck());
      }, 30000); // 30 seconds

      return () => clearInterval(interval);
    }
  }, [dispatch, autoRefresh]);

  const handleRefresh = () => {
    dispatch(fetchHealthCheck());
  };

  const handleToggleAutoRefresh = () => {
    dispatch(setAutoRefresh(!autoRefresh));
  };

  const handleClearError = () => {
    dispatch(clearError());
  };

  const getOverallStatusColor = () => {
    if (!data) return 'secondary';
    switch (data.status) {
      case 'healthy':
        return 'success';
      case 'degraded':
        return 'warning';
      case 'unhealthy':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const formatLastUpdated = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString();
    } catch {
      return 'Unknown';
    }
  };

  return (
    <div className="health-dashboard">
      <div className="health-dashboard__header">
        <h1 className="health-dashboard__title">
          🏥 TECDocChart System Health
        </h1>
        
        <div className="health-dashboard__controls">
          <button
            className="health-dashboard__refresh-btn"
            onClick={handleRefresh}
            disabled={loading}
          >
            🔄 Refresh
          </button>
          
          <label className="health-dashboard__auto-refresh">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={handleToggleAutoRefresh}
            />
            Auto-refresh (30s)
          </label>
        </div>
      </div>

      {data && (
        <div className="health-dashboard__overview">
          <div className={`health-dashboard__status health-dashboard__status--${getOverallStatusColor()}`}>
            <span className="health-dashboard__status-text">
              System Status: {data.status.toUpperCase()}
            </span>
            <span className="health-dashboard__response-time">
              Response Time: {data.response_time_ms}ms
            </span>
          </div>
          
          {lastUpdated && (
            <div className="health-dashboard__last-updated">
              Last Updated: {formatLastUpdated(lastUpdated)}
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="health-dashboard__error">
          <div className="health-dashboard__error-content">
            <span className="health-dashboard__error-icon">❌</span>
            <span className="health-dashboard__error-message">{error}</span>
            <button
              className="health-dashboard__error-dismiss"
              onClick={handleClearError}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {loading && !data && (
        <Loading size="large" message="Checking system health..." />
      )}

      {data && (
        <div className="health-dashboard__services">
          <h2 className="health-dashboard__services-title">Service Status</h2>
          
          <div className="health-dashboard__cards-grid">
            {Object.entries(data.services).map(([serviceType, serviceData]) => (
              <HealthCheckCard
                key={serviceType}
                serviceName={serviceType}
                serviceType={serviceType as ServiceType}
                serviceData={serviceData}
              />
            ))}
          </div>
        </div>
      )}

      {data && data.version && (
        <div className="health-dashboard__footer">
          <span>TECDocChart v{data.version}</span>
          {data.environment && (
            <span> • Environment: {data.environment}</span>
          )}
        </div>
      )}
    </div>
  );
};

export default HealthCheckDashboard;